import { cloneElement, isValidElement, type CSSProperties, type ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { DARK_COMPOUND, DARK_FIGURE, DARK_INK_ON_ACCENT } from './dark-tokens';
import { assertFilled } from './dev-invariants';
import { IllustrativeLabel } from './IllustrativeLabel';
import { PAPER_INK, PAPER_RULE } from './paper-tokens';
import { Surface } from './Surface';
import { CHAPTER_ACCENT_WASH, type ChapterAccentTone } from './tone';
import { Watermark } from './Watermark';

/** A single element that already declares `data-mass-root={true}` on
 * itself — the type-level half of the "no lone empty rectangle" invariant
 * (build brief §6.1 [AMENDED a1]). A caller composing an existing
 * composite (e.g. `MockChart`, whose root is a locked-contract `Surface`
 * that cannot carry extra `data-*` props) wraps it in a plain
 * `<div data-mass-root>` shell to satisfy this type — that indirection is
 * the intended composition seam, not a workaround. */
export type FilledFigureContent = ReactElement<{ 'data-mass-root': true }>;

/**
 * Visible-disclosure control for the automatically-rendered per-mode
 * `<IllustrativeLabel>` (R5-P2 design amendment §3 "Disclosure plumbing"
 * [ADV-9]). `'label'` (default) is byte-identical to every existing caller
 * — the chip renders exactly as it always has. `'none'` suppresses it
 * entirely; reserved for product-recreation figures whose OWN composing
 * component carries its own AT-honesty text instead (this file does not
 * invent one here — see `HeroJourneyFigure.tsx`'s sibling `sr-only`
 * paragraph for the established pattern elsewhere in this library). P3
 * flips the 7 Product-OS chapters to `'none'` as part of their figure
 * rebuild; this prop only ADDS the capability — nothing consumes `'none'`
 * yet.
 *
 * 2026-07-17 R7 (hallmark light-paper editorial reskin, `.claude/handoffs/
 * 2026-07-17-r7-design-contract.md` L2.2 "the identity move"): every mode's
 * OUTER chrome now sits on paper, not a dark band -- the dark interior
 * itself is UNCHANGED (`DARK_FIGURE`/`DARK_INK_ON_ACCENT` etc. stay exactly
 * as before; only what paints OUTSIDE the opaque dark panel moved to
 * `ui/paper-tokens.ts`). Mode A's outer border is now `PAPER_RULE.hairline`
 * (was a white-alpha rgba hairline) and its `AccentWash` moved from the
 * outer p-2 gutter to INSIDE the dark inner panel -- the wash used to sit
 * behind everything including that gutter, which was harmless on a dark
 * band but would bleed a color wash directly onto paper post-flip (L2.2:
 * "NO glow/shadow bleed onto paper"). Modes A/B/C's `caption` text (the
 * only mode-A/B/C/E slot that renders outside the dark interior) is
 * `PAPER_INK.muted` (was `text-white/70`) per L2.2's "captions OUTSIDE the
 * frame: ink muted, small". Mode C's triple `drop-shadow` filter is
 * REMOVED (a black-alpha shadow was near-invisible on the old dark band but
 * reads as a real glow on paper -- same "no bleed" rule). Modes B/C have
 * ZERO production consumers today (grep-verified: only mode A is composed
 * anywhere under `components/**`, via `ProductStoryFigures.tsx`'s
 * `ChapterFrame`) -- fixed for correctness/future-safety, not redesigned
 * beyond what the "no bleed on paper" rule demands. Modes D/E need NO
 * change: D's only outer content is an opaque `<Surface>` (masks its own
 * `AccentWash` completely, no caption slot); E is already a fully
 * self-contained dark card (`DARK_COMPOUND.figureModeEBackdropBg` covers
 * its whole bounds, including its caption) and never touches paper at all.
 * `IllustrativeLabel`'s own dark-only default (`surface='dark'`, not owned
 * by this lane) is UNCHANGED -- every real chapter passes
 * `disclosure="none"` so it never renders today; flagged for integration/
 * whichever lane first composes FigureFrame with `disclosure="label"` or a
 * `caption` on paper, since `IllustrativeLabel.tsx` has no paper-compatible
 * surface option yet (its existing `surface='light'` targets the OLDER,
 * cooler `ui-light/light-tokens.ts` palette, not R7's warm paper ramp).
 */
type FigureDisclosure = 'label' | 'none';

export type FigureFrameProps =
  | {
      mode: 'A';
      content: FilledFigureContent;
      fadeBottom?: boolean;
      caption?: string;
      /** P2.5 §C per-chapter accent wash — optional, absent = no wash
       * (identical to pre-amendment rendering). */
      accent?: ChapterAccentTone;
      disclosure?: FigureDisclosure;
      className?: string;
    }
  | {
      mode: 'B';
      content: FilledFigureContent;
      chromeLabel: string;
      readout: string;
      caption?: string;
      /** Accepted for API consistency across every mode (§C: "add one
       * optional field to EACH arm") but deliberately NOT rendered here —
       * mode B's own bleed-mask gradient already owns the visual field a
       * wash would compete with ("modes B and C may omit the wash if it
       * fights the mask — accent is optional per mode by design"). */
      accent?: ChapterAccentTone;
      disclosure?: FigureDisclosure;
      className?: string;
    }
  | {
      mode: 'C';
      media: FilledFigureContent;
      caption: string;
      /** Accepted, deliberately not rendered — mode C's hairline border
       * frame (R7: was a triple drop-shadow filter, removed per the R7
       * addendum above) is the visual field here; see mode B's note. */
      accent?: ChapterAccentTone;
      disclosure?: FigureDisclosure;
      className?: string;
    }
  | {
      mode: 'D';
      content: FilledFigureContent;
      copy: ReactElement;
      anchor: 'left' | 'right';
      scale?: 1.3;
      accent?: ChapterAccentTone;
      disclosure?: FigureDisclosure;
      className?: string;
    }
  | {
      mode: 'E';
      content: FilledFigureContent;
      watermark: string;
      caption: string;
      accent?: ChapterAccentTone;
      disclosure?: FigureDisclosure;
      className?: string;
    };

function assertMassRoot(el: ReactElement, slotLabel: string): void {
  if (process.env.NODE_ENV === 'production') return;
  const massRoot = isValidElement(el)
    ? (el.props as Record<string, unknown>)['data-mass-root']
    : undefined;
  assertFilled(
    massRoot === true,
    `FigureFrame: "${slotLabel}" must be a single element carrying data-mass-root={true} — ` +
      'a lone empty rectangle is banned (build brief §6.1 [AMENDED a1]).'
  );
}

/** Clones the caller's filled content, stamping `data-figure-content` onto
 * the SAME node that already carries `data-mass-root` — the measured
 * rubric (§6.4 c5) expects one `[data-figure-content][data-mass-root=true]`
 * node, not two separate wrapper/child elements. */
function stampFigureContent(el: ReactElement): ReactElement {
  return cloneElement(el, { 'data-figure-content': true } as Record<string, unknown>);
}

/**
 * The per-chapter accent wash (P2.5 §C) — a single static radial-gradient
 * behind a mode's content, `aria-hidden`, alpha-capped at 0.09 (baked
 * into `CHAPTER_ACCENT_WASH`, never re-derived here). `absent accent` ->
 * renders nothing (identical to pre-amendment). Uses a genuinely NEGATIVE
 * `-z-10` (not the spec text's literal `-z-0`, which is zero, not
 * negative) so it paints strictly behind ALL sibling content — including
 * normal-flow, non-positioned siblings like a mode's opaque inner panel —
 * per stacking-context rules, regardless of DOM order; this is what §C
 * discipline rule (b) ("never touches text-on-card contrast") actually
 * requires. Static — reduced-motion irrelevant (no animation, §C rule e).
 */
function AccentWash({ accent }: { accent: ChapterAccentTone | undefined }) {
  if (!accent) return null;
  return (
    <span
      aria-hidden="true"
      data-slot="accent-wash"
      data-accent-tone={accent}
      style={{ backgroundImage: CHAPTER_ACCENT_WASH[accent] } satisfies CSSProperties}
      className="pointer-events-none absolute inset-0 -z-10"
    />
  );
}

const BRACKET_POSITION = {
  'top-left': 'left-[14px] top-[14px] border-l border-t',
  'top-right': 'right-[14px] top-[14px] border-r border-t',
  'bottom-left': 'left-[14px] bottom-[14px] border-l border-b',
  'bottom-right': 'right-[14px] bottom-[14px] border-r border-b',
} as const;

/**
 * FigureFrame — the five-mode figure-framing primitive (R4-P2 build brief
 * §2 + §6.1 [AMENDED a1], dossier §1.3 recipes A-E). `mode` is a
 * discriminated union — each mode requires exactly the slots its recipe
 * needs, so a lone empty rectangle no longer type-checks. Every mode
 * renders exactly one `IllustrativeLabel` automatically (§6.2) whenever
 * `disclosure` is `'label'` (the default — every pre-R5-P2 caller keeps
 * this, byte-identical); `disclosure="none"` (R5-P2 §3 [ADV-9]) suppresses
 * it for product-recreation figures — see the `FigureDisclosure` type doc
 * above.
 */
export function FigureFrame(props: FigureFrameProps) {
  const { mode, className, disclosure = 'label' } = props;

  switch (mode) {
    case 'A': {
      assertMassRoot(props.content, 'content');
      const content = stampFigureContent(props.content);
      return (
        <div
          data-primitive="FigureFrame"
          data-figure-mode="A"
          className={cn(
            'relative rounded-[12px] border p-2',
            PAPER_RULE.hairline.border,
            props.fadeBottom &&
              '[mask-image:linear-gradient(to_bottom,black_0%,black_60%,transparent_100%)]',
            className
          )}
        >
          <div
            className={cn(
              'relative overflow-hidden rounded-[12px]',
              DARK_FIGURE.stop1.bg,
              DARK_COMPOUND.figureModeAInsetShadow
            )}
          >
            {/* R7: AccentWash moved inside this opaque dark panel — see the
                FigureDisclosure doc comment's R7 addendum above. */}
            <AccentWash accent={props.accent} />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-0 h-[400px] w-[400px] [mix-blend-mode:lighten]"
              style={{
                backgroundImage:
                  'radial-gradient(circle, rgba(255,255,255,.04) 0%, transparent 50%)',
              }}
            />
            {content}
          </div>
          {props.caption && (
            <p
              data-slot="caption"
              data-type-role="secondary"
              className={cn('mt-2 text-[13px]', PAPER_INK.muted.text)}
            >
              {props.caption}
            </p>
          )}
          {/* In-flow, not corner-pinned over the framed content below
              (§6.4 D1 fix direction — same mechanism as MockCard/
              MockChart/PhoneShell). */}
          {disclosure !== 'none' && (
            <IllustrativeLabel className="static bottom-auto right-auto z-auto mt-2 flex w-fit shrink-0 ml-auto" />
          )}
        </div>
      );
    }

    case 'B': {
      assertMassRoot(props.content, 'content');
      assertFilled(
        props.chromeLabel.trim().length > 0,
        'FigureFrame mode B: chromeLabel must be non-blank.'
      );
      assertFilled(
        props.readout.trim().length > 0,
        'FigureFrame mode B: readout must be non-blank.'
      );
      const content = stampFigureContent(props.content);
      return (
        <div
          data-primitive="FigureFrame"
          data-figure-mode="B"
          className={cn(
            'relative [mask-image:linear-gradient(0deg,transparent_0%,black_30%)]',
            className
          )}
        >
          <div
            className={cn(
              'mb-2 flex aspect-[11.6] items-center justify-between gap-3 rounded-[8px] border border-[#FFFFFF12] px-3',
              DARK_INK_ON_ACCENT.bg
            )}
          >
            <span
              data-slot="chrome"
              data-type-role="label"
              className="truncate text-[11px] uppercase tracking-[0.06em] text-white/40"
            >
              {props.chromeLabel}
            </span>
            <span
              data-slot="readout"
              data-type-role="tertiary"
              className="shrink-0 text-[11px] tabular-nums text-white/50"
            >
              {props.readout}
            </span>
          </div>
          {content}
          {props.caption && (
            <p
              data-slot="caption"
              data-type-role="secondary"
              className={cn('mt-2 text-[13px]', PAPER_INK.muted.text)}
            >
              {props.caption}
            </p>
          )}
          {disclosure !== 'none' && (
            <IllustrativeLabel className="static bottom-auto right-auto z-auto mt-2 flex w-fit shrink-0 ml-auto" />
          )}
        </div>
      );
    }

    case 'C': {
      assertMassRoot(props.media, 'media');
      assertFilled(
        props.caption.trim().length > 0,
        'FigureFrame mode C: caption must be non-blank.'
      );
      const media = stampFigureContent(props.media);
      return (
        <div
          data-primitive="FigureFrame"
          data-figure-mode="C"
          className={cn('relative', className)}
        >
          {/* R7: the triple drop-shadow filter is REMOVED — a black-alpha
              shadow was near-invisible on the old dark band but reads as a
              real glow bleeding onto paper post-flip (design contract L2.2:
              "NO glow/shadow bleed onto paper"). A flat hairline border
              replaces it as the frame (L2.2: "a flat 1px rule is the
              frame") — zero production consumers today, see the
              FigureDisclosure doc comment's R7 addendum. */}
          <div
            className={cn(
              'overflow-hidden rounded-t-[12px] border',
              PAPER_RULE.hairline.border,
              '[mask-image:linear-gradient(180deg,black_0%,black_50%,transparent_100%)]'
            )}
          >
            {media}
          </div>
          <p
            data-slot="caption"
            data-type-role="secondary"
            className={cn('mt-3 text-[13px]', PAPER_INK.muted.text)}
          >
            {props.caption}
          </p>
          {disclosure !== 'none' && (
            <IllustrativeLabel className="static bottom-auto right-auto z-auto mt-2 flex w-fit shrink-0 ml-auto" />
          )}
        </div>
      );
    }

    case 'D': {
      assertMassRoot(props.content, 'content');
      const scalePct = Math.round((props.scale ?? 1.3) * 100);
      const content = stampFigureContent(props.content);
      return (
        <div
          data-primitive="FigureFrame"
          data-figure-mode="D"
          className={cn('relative', className)}
        >
          <AccentWash accent={props.accent} />
          {/* ROOT CAUSE (lead defect D3): the oversize-crop viewport below
              was `absolute inset-y-0` directly inside the Surface — an
              absolutely positioned box NEVER contributes to an auto-height
              ancestor's height, no matter what `inset` values it carries,
              so this Surface previously collapsed to just its `copy`
              paragraph's height (~50px, "one-line text pile"). `min-h`
              gives the Surface a real stage.
              F2 follow-up: giving it a real stage was not enough on its
              own — with `inset-y-0` spanning the Surface's FULL height,
              the crop sat directly BEHIND `copy` (same top-left origin),
              so the copy text rendered literally on top of the mock's
              own first row at both anchors ("text-over-text"). The fix is
              a real copy BAND: Surface is now `flex-col` with `copy` as
              an ordinary in-flow (`shrink-0`) row, then a SEPARATE
              `relative` crop-viewport row that only exists BELOW it
              (`flex-1`) — the absolutely-positioned crop's `inset-y-0`
              now resolves against that viewport's own box, which starts
              only after `copy` + the `gap-3` (12px) clearance, not the
              Surface's own top. */}
          <Surface className="flex min-h-[220px] flex-col gap-3 overflow-hidden">
            <div data-slot="copy" className="relative z-10 shrink-0 px-4 pt-4">
              {props.copy}
            </div>
            <div className="relative min-h-0 flex-1 overflow-hidden">
              <div
                className={cn('absolute inset-y-0', props.anchor === 'left' ? 'left-0' : 'right-0')}
                style={{ width: `${scalePct}%` }}
              >
                {content}
              </div>
            </div>
          </Surface>
          {disclosure !== 'none' && (
            <IllustrativeLabel className="static bottom-auto right-auto z-auto mt-2 flex w-fit shrink-0 ml-auto" />
          )}
        </div>
      );
    }

    case 'E': {
      assertMassRoot(props.content, 'content');
      assertFilled(
        props.watermark.trim().length > 0,
        'FigureFrame mode E: watermark must be non-blank.'
      );
      assertFilled(
        props.caption.trim().length > 0,
        'FigureFrame mode E: caption must be non-blank.'
      );
      const content = stampFigureContent(props.content);
      return (
        <div
          data-primitive="FigureFrame"
          data-figure-mode="E"
          className={cn(
            'relative overflow-hidden rounded-[12px] border border-[#FFFFFF1A] p-6',
            DARK_COMPOUND.figureModeEBackdropBg,
            'backdrop-blur-[8px]',
            className
          )}
        >
          <AccentWash accent={props.accent} />
          {(Object.keys(BRACKET_POSITION) as (keyof typeof BRACKET_POSITION)[]).map((corner) => (
            <span
              key={corner}
              aria-hidden="true"
              data-slot="bracket"
              className={cn(
                'absolute h-[9px] w-[9px] border-[#FFFFFF1A]',
                BRACKET_POSITION[corner]
              )}
            />
          ))}
          <div
            aria-hidden="true"
            data-slot="ruler-ticks"
            className="absolute inset-x-6 top-3 h-[6px] [background-image:repeating-linear-gradient(90deg,rgba(255,255,255,.12)_0px,rgba(255,255,255,.12)_1px,transparent_1px,transparent_9px)]"
          />
          <Watermark
            word={props.watermark}
            className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
          <div className="relative z-10 flex justify-center">{content}</div>
          {/* Bottom-LEFT (not bottom-right): the auto-rendered
              IllustrativeLabel below already pins that corner — two
              absolutely-positioned bottom-right elements would overlap. */}
          <p
            data-slot="caption"
            data-type-role="label"
            className="absolute bottom-3 left-3 z-10 max-w-[40%] text-[11px] uppercase tracking-[0.06em] text-white/40"
          >
            {props.caption}
          </p>
          {disclosure !== 'none' && <IllustrativeLabel />}
        </div>
      );
    }

    default: {
      const exhaustive: never = mode;
      throw new Error(`FigureFrame: unknown mode "${String(exhaustive)}".`);
    }
  }
}
